import React, { useEffect, useState, Suspense, lazy } from "react";
import "./App.css";
import { Navigate } from "react-router-dom";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { PageSEO } from './components/SEO';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { ImpersonationBanner } from './pages/admin/AdminUserManagementPage';
import { setSessionExpiredCallback } from './services/api';
import SessionExpiredModal from './components/auth/SessionExpiredModal';
import { initSessionManager, clearSession } from './services/sessionService';

import NavigationLoader from './components/NavigationLoader';
import { ErrorBoundary } from './components/ErrorBoundary';

// Auth Protection
import { ProtectedAdminRoute, ProtectedWorkerRoute } from './components/auth/ProtectedRoute';

// Lightweight pages (eagerly loaded — small or critical path)
import EditorialCasosPage from './pages/EditorialCasosPage';
import EditorialMetodoPage from './pages/EditorialMetodoPage';
import EditorialEquipoPage from './pages/EditorialEquipoPage';
import EditorialServiciosPage from './pages/EditorialServiciosPage';
import ManifiestoPage from './pages/ManifiestoPage';
import ContactoPage from './pages/ContactoPage';
import { ArquitecturaPage } from './pages/ArquitecturaPage';
import { PrivacidadPage } from './pages/PrivacidadPage';
import { EliminarDatosPage } from './pages/EliminarDatosPage';
import { TerminosPage } from './pages/TerminosPage';
import PaymentPage from './pages/PaymentPage';

// Editorial Homepage (New Design)
import { EditorialHomepage } from './components/EditorialHomepage';

// Funnel Landing Page Components (Legacy)
import { 
  FunnelNavbar, 
  HeroSection, 
  ProblemSection,
  PortfolioShowcase,
  ServicesSection,
  MethodSection, 
  ResultsSection, 
  PortalSection, 
  PackagesSection, 
  FormSection, 
  FinalCTASection, 
  FunnelFooter,
  FloatingWhatsApp 
} from './components/funnel/FunnelSections';

// Booking Modal
import { BookingModal } from './components/BookingModal';

// Portal components (eager — login/verify are entry points)
import { PortalLogin } from './components/portal/PortalLogin';
import { PortalVerify } from './components/portal/PortalVerify';
import { PortalDashboard } from './components/portal/PortalDashboard';
import { BusinessSelector } from './components/portal/BusinessSelector';

// Unified Login (eager — critical auth path)
import UnifiedLogin from './pages/UnifiedLogin';
import UnifiedVerify from './pages/UnifiedVerify';
import AuthCallback from './pages/AuthCallback';

// iOS 26 Layout (eager — wraps all admin)
import AdminLayout from './layouts/AdminLayout';

// Worker login (eager)
import WorkerLoginPage from './pages/WorkerLoginPage';
import WorkerVerifyPage from './pages/WorkerVerifyPage';

// Chat widget (always visible)
import ChatWidget from './components/ChatWidget';

// ============================================================
// LAZY-LOADED PAGES — loaded on demand to reduce initial bundle
// ============================================================
const SurveyPage = lazy(() => import('./pages/SurveyPage'));
const AdminDashboardApple = lazy(() => import('./components/admin/AdminDashboardApple'));
const AdminDrive = lazy(() => import('./components/admin/AdminDrive').then(m => ({ default: m.AdminDrive })));
const AdminClients = lazy(() => import('./components/admin/AdminClientsSimple').then(m => ({ default: m.AdminClients })));
const AdminLogin = lazy(() => import('./components/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminCrm = lazy(() => import('./components/admin/AdminCrm').then(m => ({ default: m.AdminCrm })));
const AdminWorkers = lazy(() => import('./components/admin/AdminWorkers').then(m => ({ default: m.AdminWorkers })));
const AdminPortfolio = lazy(() => import('./components/admin/AdminPortfolio').then(m => ({ default: m.AdminPortfolio })));
const AdminMetaIntegration = lazy(() => import('./components/admin/AdminMetaIntegration').then(m => ({ default: m.AdminMetaIntegration })));
const AdminCrmList = lazy(() => import('./components/admin/AdminCrmList').then(m => ({ default: m.AdminCrmList })));
const AdminEmailMarketingPage = lazy(() => import('./pages/AdminEmailMarketingPage'));
const AdminInteractionsPage = lazy(() => import('./pages/AdminInteractionsPage'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminDailyReports = lazy(() => import('./pages/admin/AdminDailyReports'));
const AdminDeliverablesPage = lazy(() => import('./pages/admin/AdminDeliverablesPage'));
const AdminBilling = lazy(() => import('./pages/admin/AdminBilling'));
const AdminAttendancePage = lazy(() => import('./pages/admin/AdminAttendancePage'));
const MiDiaPage = lazy(() => import('./pages/admin/MiDiaPage'));
const BandejaPage = lazy(() => import('./pages/admin/BandejaPage'));
const MasPage = lazy(() => import('./pages/admin/MasPage'));
const ExecutiveDashboardPage = lazy(() => import('./pages/admin/ExecutiveDashboardPage'));
const SupervisorDashboardPage = lazy(() => import('./pages/admin/SupervisorDashboardPage'));
const CeoDashboardPage = lazy(() => import('./pages/admin/CeoDashboardPage'));
const ISO9001DashboardPage = lazy(() => import('./pages/admin/ISO9001DashboardPage'));
const LeadsV2Page = lazy(() => import('./pages/admin/LeadsV2Page'));
const ServiceCatalogPage = lazy(() => import('./pages/admin/ServiceCatalogPage'));
const ProposalsDashboardPage = lazy(() => import('./pages/admin/ProposalsDashboardPage'));
const DannaSalesDashboard = lazy(() => import('./pages/admin/DannaSalesDashboard'));
const AdminPettyCashPage = lazy(() => import('./pages/admin/AdminPettyCashPage'));
const AdminUserManagementPage = lazy(() => import('./pages/admin/AdminUserManagementPage'));
const AdminProductionPage = lazy(() => import('./pages/admin/AdminProductionPage'));
const AdminSystemToolsPage = lazy(() => import('./pages/admin/AdminSystemToolsPage'));
const ChatPage = lazy(() => import('./pages/admin/ChatPage'));
const AdminChatManagementPage = lazy(() => import('./pages/admin/AdminChatManagementPage'));
const KPIDashboardPage = lazy(() => import('./pages/admin/KPIDashboardPage'));
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'));
const ContentCalendarPage = lazy(() => import('./pages/admin/ContentCalendarPage'));
const AdminTeamPage = lazy(() => import('./pages/admin/AdminTeamPage'));
const AdminViewAsWorkerPage = lazy(() => import('./pages/admin/AdminViewAsWorkerPage'));
const TeamManagementPage = lazy(() => import('./pages/admin/TeamManagementPage'));
const EmailLifecyclePage = lazy(() => import('./pages/admin/EmailLifecyclePage'));
const VentasPage = lazy(() => import('./pages/admin/VentasPage'));
const OperacionesPage = lazy(() => import('./pages/admin/OperacionesPage'));
const FinanzasPage = lazy(() => import('./pages/admin/FinanzasPage'));
const ConfigPage = lazy(() => import('./pages/admin/ConfigPage'));
const ClientesPage = lazy(() => import('./pages/admin/ClientesPage'));
const SiteContentEditor = lazy(() => import('./components/admin/SiteContentEditor'));
const EncuestaPublicaPage = lazy(() => import('./pages/EncuestaPublicaPage'));
const AdminEncuestasPage = lazy(() => import('./pages/admin/AdminEncuestasPage'));
const TaskBoardPage = lazy(() => import('./pages/admin/TaskBoardPage'));
const ControlOperativoPage = lazy(() => import('./pages/admin/ControlOperativoPage'));
const NotificationCenterPage = lazy(() => import('./pages/admin/NotificationCenterPage'));
const WorkerPortalPageV2 = lazy(() => import('./pages/worker/WorkerPortalPageV2'));
const WorkerPipelinePage = lazy(() => import('./pages/worker/WorkerPipelinePage'));

// Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center py-20">
    <div className="animate-spin w-7 h-7 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--ios-accent)', borderTopColor: 'transparent' }} />
  </div>
);

// Site Editor Page wrapper
const SiteEditorPage = () => (
  <Suspense fallback={<PageLoader />}>
    <div>
      <div className="max-w-7xl mx-auto">
        <SiteContentEditor />
      </div>
    </div>
  </Suspense>
);

// HomePage - New Editorial Design
const HomePage = ({ onOpenBooking }) => {
  return (
    <>
      <PageSEO.Home />
      <EditorialHomepage />
    </>
  );
};

function App() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState({ open: false, email: null });

  useEffect(() => {
    // Add smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Set up session expired callback for api interceptor
    const onSessionExpired = (email) => {
      setSessionExpired({ open: true, email });
    };
    setSessionExpiredCallback(onSessionExpired);

    // Initialize session manager if user is authenticated
    let cleanupSession = null;
    const token = localStorage.getItem('portal_token');
    if (token) {
      cleanupSession = initSessionManager(onSessionExpired);
    }

    return () => {
      if (cleanupSession) cleanupSession();
    };
  }, []);

  const openBooking = () => setIsBookingOpen(true);
  const closeBooking = () => setIsBookingOpen(false);
  
  const handleCloseSessionModal = () => {
    setSessionExpired({ open: false, email: null });
    clearSession();
    window.location.href = '/login';
  };

  return (
    <HelmetProvider>
      <div className="App">
        <ImpersonationBanner />
        <Toaster position="top-center" richColors offset={70} mobileOffset={{ top: 70 }} />
        <PWAInstallPrompt />
        
        {/* Session Expired Modal - Global */}
        <SessionExpiredModal 
          isOpen={sessionExpired.open}
          email={sessionExpired.email}
          onClose={handleCloseSessionModal}
        />
        
        <BrowserRouter>
          <ErrorBoundary message="Algo salió mal al cargar la aplicación.">
          <NavigationLoader>
          <Routes>
            <Route path="/" element={<HomePage onOpenBooking={openBooking} />} />
            <Route path="/manifiesto" element={<ManifiestoPage />} />
            <Route path="/contacto" element={<ContactoPage />} />
            <Route path="/servicios" element={<><PageSEO.Casos /><EditorialServiciosPage /></>} />
            <Route path="/casos" element={<><PageSEO.Casos /><EditorialCasosPage /></>} />
            <Route path="/metodo" element={<><PageSEO.Metodo /><EditorialMetodoPage /></>} />
            <Route path="/equipo" element={<><PageSEO.Equipo /><EditorialEquipoPage /></>} />
            <Route path="/arquitectura-paliacate" element={<><PageSEO.Arquitectura /><ArquitecturaPage /></>} />
            <Route path="/privacidad" element={<><PageSEO.Privacidad /><PrivacidadPage /></>} />
            <Route path="/eliminar-datos" element={<EliminarDatosPage />} />
            <Route path="/terminos" element={<><PageSEO.Terminos /><TerminosPage /></>} />
            <Route path="/portal" element={<><PageSEO.Portal /><PortalLogin /></>} />
            <Route path="/portal/login" element={<><PageSEO.Portal /><PortalLogin /></>} />
            <Route path="/portal/verify" element={<PortalVerify />} />
            <Route path="/portal/select-business" element={<BusinessSelector />} />
            <Route path="/portal/dashboard" element={<PortalDashboard />} />
            
            {/* Public survey page - no auth required */}
            <Route path="/survey/:token" element={<Suspense fallback={<PageLoader />}><SurveyPage /></Suspense>} />
            <Route path="/encuesta" element={<Suspense fallback={<PageLoader />}><EncuestaPublicaPage /></Suspense>} />
            <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
            {/* Admin Routes — wrapped in iOS 26 AdminLayout (sidebar + header) */}
            <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
              <Route index element={<Suspense fallback={<PageLoader />}><MiDiaPage /></Suspense>} />
              <Route path="bandeja" element={<Suspense fallback={<PageLoader />}><BandejaPage /></Suspense>} />
              <Route path="mas" element={<Suspense fallback={<PageLoader />}><MasPage /></Suspense>} />
              <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><AdminDashboardApple /></Suspense>} />
              <Route path="crm" element={<Suspense fallback={<PageLoader />}><ClientesPage /></Suspense>} />
              <Route path="crm/:clientId" element={<Suspense fallback={<PageLoader />}><AdminCrm /></Suspense>} />
              <Route path="drive" element={<Suspense fallback={<PageLoader />}><AdminDrive /></Suspense>} />
              <Route path="portfolio" element={<Suspense fallback={<PageLoader />}><AdminPortfolio /></Suspense>} />
              <Route path="integrations" element={<Suspense fallback={<PageLoader />}><AdminMetaIntegration /></Suspense>} />
              <Route path="leads" element={<Suspense fallback={<PageLoader />}><LeadsV2Page /></Suspense>} />
              <Route path="pipeline" element={<Suspense fallback={<PageLoader />}><LeadsV2Page /></Suspense>} />
              <Route path="sales-pipeline" element={<Suspense fallback={<PageLoader />}><LeadsV2Page /></Suspense>} />
              <Route path="email-marketing" element={<Suspense fallback={<PageLoader />}><AdminEmailMarketingPage /></Suspense>} />
              <Route path="analytics" element={<Suspense fallback={<PageLoader />}><AdminAnalytics /></Suspense>} />
              <Route path="reports" element={<Suspense fallback={<PageLoader />}><AdminDailyReports /></Suspense>} />
              <Route path="deliverables" element={<Suspense fallback={<PageLoader />}><AdminDeliverablesPage /></Suspense>} />
              <Route path="interactions" element={<Suspense fallback={<PageLoader />}><AdminInteractionsPage /></Suspense>} />
              <Route path="production" element={<Suspense fallback={<PageLoader />}><AdminProductionPage /></Suspense>} />
              <Route path="site-editor" element={<SiteEditorPage />} />
              <Route path="billing" element={<Suspense fallback={<PageLoader />}><AdminBilling /></Suspense>} />
              <Route path="attendance" element={<Suspense fallback={<PageLoader />}><AdminAttendancePage /></Suspense>} />
              <Route path="mi-dia" element={<Suspense fallback={<PageLoader />}><MiDiaPage /></Suspense>} />
              <Route path="executive" element={<Suspense fallback={<PageLoader />}><ExecutiveDashboardPage /></Suspense>} />
              <Route path="team" element={<Suspense fallback={<PageLoader />}><AdminTeamPage /></Suspense>} />
              <Route path="team/management" element={<Suspense fallback={<PageLoader />}><TeamManagementPage /></Suspense>} />
              <Route path="team/worker/:workerId" element={<Suspense fallback={<PageLoader />}><AdminViewAsWorkerPage /></Suspense>} />
              <Route path="email-lifecycle" element={<Suspense fallback={<PageLoader />}><EmailLifecyclePage /></Suspense>} />
              <Route path="supervisor" element={<Suspense fallback={<PageLoader />}><SupervisorDashboardPage /></Suspense>} />
              <Route path="ceo" element={<Suspense fallback={<PageLoader />}><CeoDashboardPage /></Suspense>} />
              <Route path="iso9001" element={<Suspense fallback={<PageLoader />}><ISO9001DashboardPage /></Suspense>} />
              <Route path="service-catalog" element={<Suspense fallback={<PageLoader />}><ServiceCatalogPage /></Suspense>} />
              <Route path="proposals-dashboard" element={<Suspense fallback={<PageLoader />}><ProposalsDashboardPage /></Suspense>} />
              <Route path="propuestas" element={<Suspense fallback={<PageLoader />}><ProposalsDashboardPage /></Suspense>} />
              <Route path="tareas" element={<Suspense fallback={<PageLoader />}><TaskBoardPage /></Suspense>} />
              <Route path="control-operativo" element={<Suspense fallback={<PageLoader />}><ControlOperativoPage /></Suspense>} />
              <Route path="control-operativo/:clientId" element={<Suspense fallback={<PageLoader />}><ControlOperativoPage /></Suspense>} />
              <Route path="notificaciones" element={<Suspense fallback={<PageLoader />}><NotificationCenterPage /></Suspense>} />
              <Route path="encuestas" element={<Suspense fallback={<PageLoader />}><AdminEncuestasPage /></Suspense>} />
              <Route path="sales-dashboard" element={<Suspense fallback={<PageLoader />}><DannaSalesDashboard /></Suspense>} />
              <Route path="petty-cash" element={<Suspense fallback={<PageLoader />}><AdminPettyCashPage /></Suspense>} />
              <Route path="users" element={<Suspense fallback={<PageLoader />}><AdminUserManagementPage /></Suspense>} />
              <Route path="system-tools" element={<Suspense fallback={<PageLoader />}><AdminSystemToolsPage /></Suspense>} />
              <Route path="chat" element={<Suspense fallback={<PageLoader />}><ChatPage /></Suspense>} />
              <Route path="chat/manage" element={<Suspense fallback={<PageLoader />}><AdminChatManagementPage /></Suspense>} />
              <Route path="kpis" element={<Suspense fallback={<PageLoader />}><KPIDashboardPage /></Suspense>} />
              <Route path="pdf-reports" element={<Suspense fallback={<PageLoader />}><ReportsPage /></Suspense>} />
              <Route path="content-calendar" element={<Suspense fallback={<PageLoader />}><ContentCalendarPage /></Suspense>} />
              <Route path="ventas" element={<Suspense fallback={<PageLoader />}><VentasPage /></Suspense>} />
              <Route path="operaciones" element={<Suspense fallback={<PageLoader />}><OperacionesPage /></Suspense>} />
              <Route path="finanzas" element={<Suspense fallback={<PageLoader />}><FinanzasPage /></Suspense>} />
              <Route path="config" element={<Suspense fallback={<PageLoader />}><ConfigPage /></Suspense>} />
            </Route>
            {/* Unified Login */}
            <Route path="/login" element={<UnifiedLogin />} />
            <Route path="/login/verify" element={<UnifiedVerify />} />
            <Route path="/verify" element={<UnifiedVerify />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            {/* Worker Portal */}
            <Route path="/worker" element={<WorkerLoginPage />} />
            <Route path="/worker/login" element={<WorkerLoginPage />} />
            <Route path="/worker/verify" element={<WorkerVerifyPage />} />
            <Route path="/worker/dashboard" element={<ProtectedWorkerRoute><Suspense fallback={<PageLoader />}><WorkerPortalPageV2 /></Suspense></ProtectedWorkerRoute>} />
            <Route path="/worker/pipeline" element={<ProtectedWorkerRoute><Suspense fallback={<PageLoader />}><WorkerPipelinePage /></Suspense></ProtectedWorkerRoute>} />
            <Route path="/worker/chat" element={<ProtectedWorkerRoute><Suspense fallback={<PageLoader />}><ChatPage /></Suspense></ProtectedWorkerRoute>} />
            {/* Payment Pages */}
            <Route path="/pay/:linkId" element={<PaymentPage />} />
            <Route path="/payment/success" element={<PaymentPage />} />
            <Route path="/payment/cancel" element={<PaymentPage />} />
          </Routes>
        
          {/* Global Booking Modal */}
          <BookingModal isOpen={isBookingOpen} onClose={closeBooking} />
          <ChatWidget />
          </NavigationLoader>
          </ErrorBoundary>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
